import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Col,
  Flex,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Space,
  Switch,
  Table,
  Tag,
  message,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import {
  notificationTimeout,
  notificationType,
  scrollHeight,
} from '../common/constants';
import notValid from '../common/notValid';
import pluralise from '../common/pluralise';
import {
  firGetVehicles,
  firAddVehicle,
  firUpdateVehicle,
  firDeleteVehicle,
} from '../firebase';

export default function SiteConfigVehicles({ siteId }) {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);

  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const { timeoutError, timeoutSuccess } = notificationTimeout;
  const { typeError, typeSuccess } = notificationType;

  const openMessage = useCallback(
    (content, duration, type) => {
      messageApi.open({ content, duration, type });
    },
    [messageApi],
  );

  const fetchVehicles = useCallback(
    async (nameSite) => {
      if (notValid(nameSite)) return;
      setLoading(true);
      try {
        const result = await firGetVehicles(nameSite);
        setVehicles(result);
        // D24 (2026-05-13): no success toast on automatic loads.
      } catch (error) {
        console.error('-- Error fetching vehicles:', error);
        openMessage('Error fetching vehicles', timeoutError, typeError);
      } finally {
        setLoading(false);
      }
    },
    [openMessage, timeoutError, typeError],
  );

  const handleAdd = () => {
    setEditingVehicle(null);
    form.resetFields();
    form.setFieldsValue({ isWhitelist: true });
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    setEditingVehicle(record);
    form.setFieldsValue({
      plateNumber: record.id,
      blockCode: record.blockCode || '',
      unitId: record.unitId || '',
      ownerName: record.ownerName || '',
      isWhitelist: record.isWhitelist ?? true,
    });
    setModalOpen(true);
  };

  const handleDelete = async (plateNumber) => {
    try {
      await firDeleteVehicle(siteId, plateNumber);
      openMessage('Vehicle deleted', timeoutSuccess, typeSuccess);
      fetchVehicles(siteId);
    } catch (error) {
      console.error('-- Error deleting vehicle:', error);
      openMessage('Error deleting vehicle', timeoutError, typeError);
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const { plateNumber, blockCode, unitId, ownerName, isWhitelist } = values;
      const now = new Date();
      const dateAdded = now.toISOString().split('T')[0];
      const timeAdded = now.toTimeString().split(' ')[0];

      const vehicleData = {
        isWhitelist: isWhitelist ?? true,
        blockCode: blockCode || '',
        unitId: unitId || '',
        ownerName: ownerName || '',
      };

      if (editingVehicle) {
        await firUpdateVehicle(siteId, editingVehicle.id, vehicleData);
        openMessage('Vehicle updated', timeoutSuccess, typeSuccess);
      } else {
        await firAddVehicle(siteId, plateNumber.toUpperCase(), {
          ...vehicleData,
          dateAdded,
          timeAdded,
        });
        openMessage('Vehicle added', timeoutSuccess, typeSuccess);
      }

      setModalOpen(false);
      form.resetFields();
      fetchVehicles(siteId);
    } catch (error) {
      if (error.errorFields) return;
      console.error('-- Error saving vehicle:', error);
      openMessage('Error saving vehicle', timeoutError, typeError);
    }
  };

  useEffect(() => {
    fetchVehicles(siteId);
  }, [fetchVehicles, siteId]);

  const columns = [
    {
      title: 'Plate Number',
      dataIndex: 'id',
      width: 140,
      sorter: (a, b) => a.id.localeCompare(b.id),
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Block',
      dataIndex: 'blockCode',
      width: 100,
      render: (val) => val || '--',
    },
    {
      title: 'Unit',
      dataIndex: 'unitId',
      width: 140,
      render: (val) => val || '--',
    },
    {
      title: 'Owner',
      dataIndex: 'ownerName',
      width: 160,
      render: (val) => val || '--',
    },
    {
      title: 'Whitelisted',
      dataIndex: 'isWhitelist',
      width: 120,
      render: (val) =>
        val ? (
          <Tag color="success">Yes</Tag>
        ) : (
          <Tag color="error">No</Tag>
        ),
      filters: [
        { text: 'Whitelisted', value: true },
        { text: 'Not Whitelisted', value: false },
      ],
      onFilter: (value, record) => record.isWhitelist === value,
    },
    {
      title: 'Date Added',
      dataIndex: 'dateAdded',
      width: 130,
    },
    {
      title: 'Actions',
      width: 180,
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
            type="link"
          >
            Edit
          </Button>
          <Popconfirm
            title={`Delete vehicle ${record.id}?`}
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            okType="danger"
          >
            <Button danger icon={<DeleteOutlined />} size="small" type="link">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <Flex justify="flex-end" style={{ marginBottom: 12 }}>
        <Button icon={<PlusOutlined />} onClick={handleAdd} type="primary">
          Add Vehicle
        </Button>
      </Flex>
      <Table
        columns={columns}
        dataSource={vehicles}
        loading={loading}
        pagination={{
          position: ['bottomCenter'],
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]} - ${range[1]} of ${total} ${pluralise(total, 'vehicle')}`,
        }}
        rowKey="id"
        scroll={{ x: 800, y: scrollHeight }}
      />

      <Modal
        title={editingVehicle ? 'Edit Vehicle' : 'Add Vehicle'}
        open={modalOpen}
        onOk={handleModalOk}
        onCancel={() => {
          setModalOpen(false);
          form.resetFields();
        }}
        okText={editingVehicle ? 'Update' : 'Add Vehicle'}
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            label="Plate Number"
            name="plateNumber"
            rules={[{ required: true, message: 'Please enter a plate number' }]}
          >
            <Input
              disabled={!!editingVehicle}
              placeholder="e.g., SBB8888T"
              style={{ textTransform: 'uppercase' }}
            />
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Block Code" name="blockCode">
                <Input placeholder="e.g., 02" />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item
                label="Unit ID"
                name="unitId"
                tooltip="Full unit identifier, e.g., 02-08-07"
              >
                <Input placeholder="e.g., 02-08-07" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Owner Name" name="ownerName">
            <Input placeholder="Optional" />
          </Form.Item>
          <Form.Item
            label="Whitelisted"
            name="isWhitelist"
            valuePropName="checked"
          >
            <Switch
              checkedChildren={<CheckOutlined />}
              unCheckedChildren={<CloseOutlined />}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

SiteConfigVehicles.propTypes = {
  siteId: PropTypes.string.isRequired,
};
